import React from "react";
import { useTranslation } from "react-i18next";
import SkeletonBlock from "./SkeletonBlock";
import { numOf, topItem, sumValues, fmt, trendFor } from "./kpiHelpers";

// Single-attribute KPI strip (Clarity/Color/Cut/Shape/Certificate/Size/Clicks).
// Same edr-kpis/edr-kpi markup as ReportKpiBand so both report families read
// as one visual system, driven off a single attribute slice instead of the
// full multi-attribute Extended payload.
const ReportKpiStrip = ({ report, loading, config }) => {
  const { t } = useTranslation();

  if (loading) {
    const skeletonCount =
      3 + (config.hideTotalDimensionSum ? 0 : 1) + (config.noiseFilter ? 1 : 0) + (config.showTop3Concentration ? 1 : 0);
    return (
      <div className="edr-kpis">
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <SkeletonBlock className="edr-kpi-skeleton" key={index} />
        ))}
      </div>
    );
  }

  const totalClicksKey = config.totalClicksKey || "TotalDiamondClicks";
  const summaryKpiKey = config.summaryKpiKey || "DiamondSummaryKPIs";
  const totalClicks = report?.[totalClicksKey]?.[0]?.TotalClickCnt || 0;
  const list = report?.[config.dataKey] || [];
  const totalSearches = sumValues(list, config.valueField);
  const top = topItem(list, config.labelField, config.valueField);
  const distinctValues = list.filter((item) => numOf(item[config.valueField]) > 0).length;

  const clicksTrend = trendFor(report?.[summaryKpiKey], "TotalClicks");

  const kpis = [{ lab: t("Total Clicks"), val: fmt(totalClicks), trend: clicksTrend }];
  if (!config.hideTotalDimensionSum) {
    kpis.push({ lab: t("Total {{dim}} Searches", { dim: t(config.dimensionLabel) }), val: fmt(totalSearches) });
  }
  kpis.push({
    lab: t("Top {{dim}}", { dim: t(config.dimensionLabel) }),
    val: top?.label,
    sub: top && `${fmt(top.value)} · ${top.percentage}%`,
  });

  if (config.showTop3Concentration) {
    const top3Sum = [...list]
      .sort((a, b) => numOf(b[config.valueField]) - numOf(a[config.valueField]))
      .slice(0, 3)
      .reduce((sum, item) => sum + numOf(item[config.valueField]), 0);
    const top3Pct = totalSearches > 0 ? Math.round((top3Sum / totalSearches) * 100) : 0;
    kpis.push({
      lab: t("Top-3 Concentration"),
      val: `${top3Pct}%`,
      sub: t("of all clicks in top 3"),
    });
  }

  kpis.push({ lab: t("Distinct Values"), val: distinctValues, sub: t("unique {{dim}}s searched", { dim: t(config.dimensionLabel)?.toLowerCase() }) });

  if (config.noiseFilter) {
    const flaggedCount = list
      .filter((item) => !config.noiseFilter.isValid(item[config.labelField]))
      .reduce((sum, item) => sum + numOf(item[config.valueField]), 0);
    kpis.push({
      lab: t("Flagged / Grouped"),
      val: fmt(flaggedCount),
      sub: t("invalid values bucketed into Others"),
    });
  }

  return (
    <div className="edr-kpis">
      {kpis.map((kpi, index) => (
        <div className="edr-kpi" key={index}>
          <div className="edr-kpi-lab">{kpi.lab}</div>
          <div className="edr-kpi-val">{kpi.val ?? "—"}</div>
          {kpi.sub && <div className="edr-kpi-sub">{kpi.sub}</div>}
          {kpi.trend && (
            <div className={`edr-kpi-trend ${kpi.trend.up ? "edr-up" : "edr-down"}`}>
              {kpi.trend.up ? "▲" : "▼"} {Math.abs(kpi.trend.change).toFixed(1)}% {t("vs prior period")}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ReportKpiStrip;
